import React, { useState } from "react";
import axios from "axios";
import { Loader2, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "firstName", label: "First name", type: "text", placeholder: "Jane" },
  { key: "lastName", label: "Last name", type: "text", placeholder: "Smith" },
  { key: "email", label: "Email address", type: "email", placeholder: "jane@company.com" },
  { key: "mobile", label: "Mobile number", type: "tel", placeholder: "0400 000 000" },
  { key: "code", label: "Access code", type: "text", placeholder: "TR-XXXXXX" },
];

const Gate = ({ onGranted }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", mobile: "", code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (Object.values(form).some((v) => !v.trim())) {
      setError("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/access/verify`, form);
      if (data.success) {
        localStorage.setItem("resumeAccessGranted", "true");
        localStorage.setItem("resumeAccessName", `${form.firstName} ${form.lastName}`);
        onGranted();
      } else {
        setError(data.message || "Invalid access code.");
      }
    } catch (_err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span
            className="text-[var(--ink)] block"
            style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 500, fontSize: "1.8rem", letterSpacing: "0.18em" }}
          >
            RÉSUMÉ
          </span>
        </div>

        <div className="bg-white rounded-xl shadow-[0_20px_60px_-15px_rgba(30,32,80,0.25)] ring-1 ring-black/5 p-7">
          <div className="flex items-center gap-2 mb-1 text-[var(--ink)]">
            <Lock size={18} />
            <h2 className="text-[15px] font-semibold uppercase tracking-wider">Private Access</h2>
          </div>
          <p className="text-[13px] text-[var(--muted)] mb-5">
            Please enter your details and access code to view the resume.
          </p>

          <form onSubmit={submit} className="space-y-3.5">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={f.key} className="text-[13px] text-[var(--text)]">{f.label}</Label>
                <Input
                  id={f.key}
                  type={f.type}
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={update(f.key)}
                  className={f.key === "code" ? "uppercase" : ""}
                  autoComplete={f.key === "code" ? "off" : "on"}
                />
              </div>
            ))}

            {error && (
              <p className="text-[13px] text-destructive font-medium">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--ink)] hover:bg-[var(--ink-dark)] text-white"
            >
              {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
              {loading ? "Verifying…" : "View Resume"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Gate;
